import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface UserDialogsProps {
  // Suspend Dialog
  suspendDialogOpen: boolean;
  onSuspendClose: () => void;
  selectedUser: any;
  suspendForm: {
    reason: string;
    duration: string;
    customDays: number;
  };
  setSuspendForm: (form: any) => void;
  onSuspendUser: () => void;
  
  // Bulk Suspend Dialog
  bulkSuspendOpen: boolean;
  onBulkSuspendClose: () => void;
  selectedUsersCount: number;
  onBulkSuspend: () => void;
  
  // Create Seller Dialog
  createSellerOpen: boolean;
  onCreateSellerClose: () => void;
  createSellerForm: any;
  setCreateSellerForm: (form: any) => void;
  createSellerError: string;
  setCreateSellerError: (error: string) => void;
  onCreateSeller: () => void;
  
  processing: boolean;
}

const UserDialogs: React.FC<UserDialogsProps> = ({
  suspendDialogOpen,
  onSuspendClose,
  selectedUser,
  suspendForm,
  setSuspendForm,
  onSuspendUser,
  bulkSuspendOpen,
  onBulkSuspendClose,
  selectedUsersCount,
  onBulkSuspend,
  createSellerOpen,
  onCreateSellerClose,
  createSellerForm,
  setCreateSellerForm,
  createSellerError,
  setCreateSellerError,
  onCreateSeller,
  processing,
}) => {
  const { t } = useTranslation();

  const handleCreateSellerChange = (field: string, value: string) => {
    setCreateSellerForm({ ...createSellerForm, [field]: value });
  };

  return (
    <>
      {/* Suspend Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onClose={onSuspendClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px', bgcolor: 'background.paper' } }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {t('admin.suspendUser')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('admin.thisWillSuspendUser', { email: selectedUser?.email })}
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('admin.reasonForSuspension')}
              value={suspendForm.reason}
              onChange={(e) => setSuspendForm({ ...suspendForm, reason: e.target.value })}
              placeholder={t('admin.enterReasonForSuspension')}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>{t('admin.suspensionDuration')}</InputLabel>
              <Select
                value={suspendForm.duration}
                onChange={(e) => setSuspendForm({ ...suspendForm, duration: e.target.value })}
                label={t('admin.suspensionDuration')}
              >
                <MenuItem value="permanent">{t('admin.permanent')}</MenuItem>
                <MenuItem value="custom">{t('admin.customDuration')}</MenuItem>
              </Select>
            </FormControl>
            {suspendForm.duration === 'custom' && (
              <TextField
                fullWidth
                type="number"
                label={t('admin.days')}
                value={suspendForm.customDays}
                onChange={(e) => setSuspendForm({ ...suspendForm, customDays: parseInt(e.target.value) || 1 })}
                sx={{ mt: 2 }}
                inputProps={{ min: 1 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onSuspendClose}>{t('common.cancel')}</Button>
          <Button
            onClick={onSuspendUser}
            variant="contained"
            color="warning"
            disabled={!suspendForm.reason || processing}
          >
            {processing ? <CircularProgress size={20} /> : t('admin.suspendUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog
        open={bulkSuspendOpen}
        onClose={onBulkSuspendClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px', bgcolor: 'background.paper' } }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {t('admin.bulkSuspend')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('admin.youAreAboutToSuspend', { count: selectedUsersCount })}
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('admin.reasonForSuspension')}
              value={suspendForm.reason}
              onChange={(e) => setSuspendForm({ ...suspendForm, reason: e.target.value })}
              placeholder={t('admin.enterReasonForSuspension')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onBulkSuspendClose}>{t('common.cancel')}</Button>
          <Button
            onClick={onBulkSuspend}
            variant="contained"
            color="warning"
            disabled={!suspendForm.reason || processing}
          >
            {processing ? <CircularProgress size={20} /> : t('admin.suspendXUsers', { count: selectedUsersCount })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Seller Dialog */}
      <Dialog
        open={createSellerOpen}
        onClose={onCreateSellerClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px', bgcolor: 'background.paper' } }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {t('admin.createSeller', { defaultValue: 'Create Seller Account' })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {createSellerError && (
              <Alert severity="error" onClose={() => setCreateSellerError('')}>
                {createSellerError}
              </Alert>
            )}
            <TextField
              fullWidth
              label={t('common.email')}
              type="email"
              value={createSellerForm.email}
              onChange={(e) => handleCreateSellerChange('email', e.target.value)}
            />
            <TextField
              fullWidth
              label={t('auth.password')}
              type="password"
              value={createSellerForm.password}
              onChange={(e) => handleCreateSellerChange('password', e.target.value)}
            />
            <TextField
              fullWidth
              label={t('auth.confirmPassword', { defaultValue: 'Confirm password' })}
              type="password"
              value={createSellerForm.confirmPassword}
              onChange={(e) => handleCreateSellerChange('confirmPassword', e.target.value)}
            />
            <TextField
              fullWidth
              label={t('profile.fullName', { defaultValue: 'Full name' })}
              value={createSellerForm.full_name}
              onChange={(e) => handleCreateSellerChange('full_name', e.target.value)}
            />
            <TextField
              fullWidth
              label={t('profile.companyName', { defaultValue: 'Company name' })}
              value={createSellerForm.company_name}
              onChange={(e) => handleCreateSellerChange('company_name', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCreateSellerClose}>{t('common.cancel')}</Button>
          <Button onClick={onCreateSeller} variant="contained" disabled={processing}>
            {processing ? <CircularProgress size={20} /> : t('admin.createSeller', { defaultValue: 'Create Seller' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDialogs;